<template>
  <article data-component-name="Cards.Event">
    <div class="top">
      <AppTag v-if="isCountdownVisible" variant="orange">
        {{ countdownLabel }}
      </AppTag>

      <AppTag v-if="tag" :variant="tag.variant">
        {{ $t(tag.i18nKey) }}
      </AppTag>
    </div>

    <div class="middle">
      <strong>{{ event.title }}</strong>

      <time :datetime="eventDate">
        {{ formattedWithTimezone }}
      </time>

      <div v-html="event.preview" class="description" />

      <address>
        <SvgIcon name="location" fill="inherit" />
        <span>{{ event.location }}</span>
      </address>
    </div>

    <div class="bottom">
      <Buttons.Link
        v-if="hasPreviewLink"
        :to="link"
        external
        target="_blank"
        fill="outline"
        accent="orange"
        size="medium"
      >
        {{ $t('Learn_more') }}
        <SvgIcon name="arrow-right-up" fill="inherit" />
      </Buttons.Link>

      <Buttons.Link
        v-else
        :to="localePathWithSlash(link)"
        fill="outline"
        accent="orange"
      >
        {{ $t('Learn_more') }}
        <SvgIcon name="arrow-forward" fill="inherit" />
      </Buttons.Link>
    </div>
  </article>
</template>

<script setup lang="ts">
// components
import AppTag from '~/components/redesign/AppTag.vue';
import Buttons from '~/components/redesign/Buttons';

// composables
import { useDayjs } from '#dayjs';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// constants
import { EventTag } from '~/types/events';
import { PATHS } from '~/constants/nav-items';

// utils
import get from 'lodash.get';
import pick from 'lodash.pick';
import { getEventFormat, getTimezoneAbbr } from '~/utils';

const props = defineProps<{ event: unknown }>();

const { t } = useI18n();
const dayjs = useDayjs();
const localePathWithSlash = useLocalePathWithSlash();

const tag = computed(
  () => EventTag[getEventFormat(pick(props.event, ['type', 'status']))],
);
const hasPreviewLink = computed(
  () => typeof get(props.event, 'data.links.preview') === 'string',
);

const link = computed(() =>
  hasPreviewLink.value
    ? get(props.event, 'data.links.preview')
    : `${PATHS.EVENTS.path}${props.event.slug}`,
);

const eventDate = computed(() => get(props.event, 'event_date'));
const eventTimezone = computed(() => get(props.event, 'timezone'));

const differenceInDays = computed(() =>
  dayjs
    .utc(eventDate.value)
    .startOf('day')
    .diff(dayjs.utc().startOf('day'), 'day'),
);

const isCountdownVisible = computed(
  () => differenceInDays.value > -1 && differenceInDays.value <= 15,
);

const countdownLabel = computed(() =>
  differenceInDays.value === 0
    ? t('Cards.Event.live-today')
    : t('Cards.Event.countdown-tag', { count: differenceInDays.value }),
);

const formattedWithTimezone = computed(() => {
  const formatted = dayjs
    .utc(eventDate.value)
    .local()
    .format('MMMM DD, YYYY | hh:mm a');
  const tzAbbr = getTimezoneAbbr(eventTimezone.value);

  return `${formatted} ${tzAbbr}`;
});
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/mixins/component';
@use '_/mixins/text-transform';
@use '_/fn';

[data-component-name='Cards.Event'] {
  padding: 1.5rem 1rem;
  border: 1px solid fn.token('outline-secondary');

  @include flex.start-start;

  flex-direction: column;

  @include media.tablet {
    padding: 1rem;
  }

  @include media.mobile {
    padding: 1rem;
  }

  .top {
    @include flex.center-start;

    gap: 0 0.5rem;
    margin-bottom: 0.5rem;
  }

  .middle {
    margin-bottom: 1.5rem;

    strong {
      display: block;
      margin-bottom: 0.5rem;

      @include typo.subtitle-1;
    }

    time {
      display: block;
      color: fn.token('text-secondary');
      padding: 0.25rem 0;
      margin-bottom: 0.5rem;

      @include typo.body-3;
    }

    :deep(.description) {
      color: fn.token('text-primary');
      margin: 1rem 0;

      @include typo.body-3;
      @include text-transform.truncate-to-rows(4);

      a {
        @include component.app-link;
      }
    }

    address {
      padding: 0.5rem 0;
      gap: 0 0.25rem;

      @include flex.center-start;

      svg {
        @include size.fixed(1.25rem);
        fill: fn.token('icon-secondary');
      }

      span {
        font-style: normal;
        color: fn.token('text-secondary');

        @include typo.body-3;
      }
    }
  }

  .bottom {
    @include media.mobile {
      width: 100%;
    }
  }
}
</style>
